// ---------------------------------------------------------------------------
// DiCharts – Tick Aggregator
// ---------------------------------------------------------------------------
// Converts a stream of price ticks into OHLC candles at a configurable
// interval.  Each tick updates the "forming" candle; when the interval
// boundary is crossed a completed candle is emitted.
// ---------------------------------------------------------------------------

import type { OHLCTuple } from '../types';

export interface Tick {
  timestamp: number; // epoch ms
  price: number;
  volume?: number;
}

export interface TickAggregator {
  /**
   * Process a single tick.
   * Returns the completed candle when a new period starts, otherwise `null`.
   */
  processTick(tick: Tick): OHLCTuple | null;
  /** The candle currently being formed. */
  currentCandle(): OHLCTuple | null;
  /** Change the aggregation interval (resets state). */
  setInterval(intervalMs: number): void;
  /** Reset internal state. */
  reset(): void;
}

export function createTickAggregator(intervalMs: number): TickAggregator {
  let _interval = intervalMs;
  let _candleStart = 0;
  let _open = 0;
  let _high = -Infinity;
  let _low = Infinity;
  let _close = 0;
  let _active = false;

  function bucketStart(ts: number): number {
    return Math.floor(ts / _interval) * _interval;
  }

  function currentAsTuple(): OHLCTuple {
    return [_candleStart, _open, _high, _low, _close];
  }

  function processTick(tick: Tick): OHLCTuple | null {
    const ts = tick.timestamp;
    const price = tick.price;
    const bucket = bucketStart(ts);

    if (!_active) {
      // First tick ever.
      _candleStart = bucket;
      _open = price;
      _high = price;
      _low = price;
      _close = price;
      _active = true;
      return null;
    }

    if (bucket > _candleStart) {
      // New period – emit completed candle and start fresh.
      const completed = currentAsTuple();

      _candleStart = bucket;
      _open = price;
      _high = price;
      _low = price;
      _close = price;

      return completed;
    }

    // Same period – update OHLC.
    if (price > _high) _high = price;
    if (price < _low) _low = price;
    _close = price;

    return null;
  }

  function currentCandle(): OHLCTuple | null {
    if (!_active) return null;
    return currentAsTuple();
  }

  function setInterval(ms: number) {
    _interval = ms;
    reset();
  }

  function reset() {
    _active = false;
    _candleStart = 0;
    _open = 0;
    _high = -Infinity;
    _low = Infinity;
    _close = 0;
  }

  return { processTick, currentCandle, setInterval, reset };
}
